import React, { useState } from "react";
import { Table, Input, Space, Button } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useProjectState } from "../../../context/stateContext";
import OrdersInfoModal from "./OrdersInfoModal";
import tablesInfoActions from "../../../redux/tablesInfo/actions";
import OrderInfoStatistics from "./OrderInfoStatistics";
import { formatDriversTurnOverResponse } from "./FormatDriversTurnOverResponse";
import "./index.css";

const { getBillDetailsModal } = tablesInfoActions;

const OrdersInfoTable = () => {
  const [selectedRecord, setSelectedRecord] = useState({});
  const dispatch = useDispatch();

  const {
    setShowOrderDetails,
    //  showPaymentMethods
  } = useProjectState();
  const { driversTurnOver, loading } = useSelector(
    (state) => state.driversReducer
  );

  const newArray = formatDriversTurnOverResponse(driversTurnOver);

  const getFilterProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="По-голямо от"
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => {
            confirm();
          }}
          style={{ width: 195, marginBottom: 10, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
          >
            Филтрирай
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
          >
            Изчисти
          </Button>
        </Space>
      </div>
    ),
    onFilter: (value, record) => record[dataIndex] >= parseInt(value),
  });

  let closed = 0;
  let executed = 0;
  let moved = 0;
  let totalTime = 0;
  let created = 0;
  let result = 0;
  for (let row of newArray) {
    closed += row.closed;
    executed += row.executed;
    moved += typeof row.moved === "number" ? row.moved : 0;
    totalTime += row.totalTime;
    created += typeof row.created === "number" ? row.created : 0;
    result += typeof row.result === "number" ? row.result : 0;
  }

  const showResultPositiveMinutes = (result) => {
    if (result === "-") {
      return result;
    } else if (result > 0) {
      return `+${result} мин.`;
    } else {
      return `${result} мин.`;
    }
  };

  const getAverageTime = (totalTime, columnName) => {
    const dataLengthWithoutZeros = newArray.filter(
      (item) => typeof item[columnName] === "number"
    ).length;
    const averageTimePerInstance = totalTime / dataLengthWithoutZeros;

    const minutes = Math.floor(averageTimePerInstance);
    let seconds = Math.round((averageTimePerInstance - minutes) * 60);
    seconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}.${seconds}`;
  };

  const columns = [
    {
      title: "Номер",
      dataIndex: "tableNumber",
      rowKey: "tableNumber",
      sorter: (a, b) => a.tableNumber.localeCompare(b.tableNumber),
      key: "tableNumber",
      align: "center",
      render: (tableNumber) => (
        <span className="orders-info-table-text">{tableNumber}</span>
      ),
    },
    {
      title: "Получена",
      dataIndex: "accepted",
      rowKey: "accepted",
      sorter: (a, b) =>
        dayjs(a.accepted, "DD.MM - HH:mm:ss").diff(
          dayjs(b.accepted, "DD.MM - HH:mm:ss")
        ),
      key: "accepted",
      align: "center",
      render: (accepted) => (
        <span className="orders-info-table-text">{accepted}</span>
      ),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Открита</span>
            <span>
              {created !== 0
                ? `${getAverageTime(created, "created")} мин.`
                : ""}
            </span>
          </div>
        );
      },
      dataIndex: "created",
      rowKey: "created",
      sorter: (a, b) => dayjs(a.created).diff(dayjs(b.created)),
      key: "created",
      align: "center",
      render: (created) => (
        <span className="orders-info-table-text">
          {typeof created === "string" ? created : created + " мин."}
        </span>
      ),
      ...getFilterProps("created"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Изпълнена</span>
            <span>
              {executed !== 0
                ? `${getAverageTime(executed, "executed")} мин.`
                : ""}
            </span>
          </div>
        );
      },
      dataIndex: "executed",
      rowKey: "executed",
      sorter: (a, b) => dayjs(a.executed).diff(dayjs(b.executed)),
      key: "executed",
      align: "center",
      render: (executed) => (
        <span className="orders-info-table-text">{executed} мин.</span>
      ),
      ...getFilterProps("executed"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Назначена</span>
            <span>
              {moved !== 0 ? `${getAverageTime(moved, "moved")} мин.` : ""}
            </span>
          </div>
        );
      },
      dataIndex: "moved",
      rowKey: "moved",
      sorter: (a, b) => a.moved - b.moved,
      key: "moved",
      align: "center",
      render: (moved) => (
        <span className="orders-info-table-text">
          {moved !== "-" ? moved + " мин." : moved}
        </span>
      ),
      ...getFilterProps("moved"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Закрита</span>
            <span>
              {closed ? `${getAverageTime(closed, "closed")} мин.` : ""}
            </span>
          </div>
        );
      },
      dataIndex: "closed",
      rowKey: "closed",
      sorter: (a, b) => a.closed - b.closed,
      key: "closed",
      align: "center",
      render: (closed) => (
        <span className="orders-info-table-text">{closed} мин.</span>
      ),
      ...getFilterProps("closed"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Резултат</span>
            <span>
              {result !== 0 ? `${getAverageTime(result, "result")} мин.` : ""}
            </span>
          </div>
        );
      },
      dataIndex: "result",
      rowKey: "result",
      sorter: (a, b) => dayjs(a.result).diff(dayjs(b.result)),
      key: "result",
      align: "center",
      render: (result) => (
        <span className="orders-info-table-text">
          {showResultPositiveMinutes(result)}
        </span>
      ),
      ...getFilterProps("result"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Общо време</span>
            <span>
              {totalTime !== 0
                ? `${getAverageTime(totalTime, "totalTime")} мин.`
                : ""}
            </span>
          </div>
        );
      },
      dataIndex: "totalTime",
      rowKey: "totalTime",
      sorter: (a, b) => a.totalTime - b.totalTime,
      key: "totalTime",
      align: "center",
      render: (totalTime) => (
        <span className="orders-info-table-text">{totalTime} мин.</span>
      ),
      ...getFilterProps("totalTime"),
    },
    {
      title: () => {
        return (
          <div className="orders-info-table-column-title">
            <span>Разстояние</span>
          </div>
        );
      },
      dataIndex: "distanceBetweenLocationsInMeters",
      rowKey: "distanceBetweenLocationsInMeters",
      sorter: (a, b) => {
        if (
          a.distanceBetweenLocationsInMeters === "-" &&
          b.distanceBetweenLocationsInMeters === "-"
        ) {
          return 0;
        }
        if (a.distanceBetweenLocationsInMeters === "-") {
          return 1; // Place '-' at the end
        }
        if (b.distanceBetweenLocationsInMeters === "-") {
          return -1; // Place '-' at the end
        }
        return (
          a.distanceBetweenLocationsInMeters -
          b.distanceBetweenLocationsInMeters
        );
      },
      key: "distanceBetweenLocationsInMeters",
      align: "center",
      render: (distanceBetweenLocationsInMeters) => (
        <span className="orders-info-table-text">
          {typeof distanceBetweenLocationsInMeters === "number"
            ? `${Math.round(distanceBetweenLocationsInMeters)} метра`
            : "-"}
        </span>
      ),
      ...getFilterProps("distanceBetweenLocationsInMeters"),
    },
  ];

  const getBillDetails = (record) => {
    dispatch(getBillDetailsModal(record.billId));
    setSelectedRecord(
      driversTurnOver.filter((data) => data.billId === record.billId)
    );
    setShowOrderDetails(true);
  };

  return (
    <>
      <OrderInfoStatistics data={newArray} />
      <div className="orders-info-table-container">
        <Table
          title={() => (
            <div className="order-info-table-title">
              <InfoCircleOutlined />{" "}
              <span>
                Сумите в колоните са средното време за изпълнение на операцията
                за периода.
              </span>
            </div>
          )}
          size="small"
          loading={loading}
          rowKey="key"
          showHeader
          dataSource={newArray}
          columns={columns}
          pagination={false}
          className="orders-info-table"
          rowClassName={(record) =>
            record.result >= 10
              ? "orders-info-table-row-red-background"
              : record.result < 10 && record.result > 0
              ? "orders-info-table-row-orange-background"
              : ""
          }
          onRow={(record) => {
            return {
              onClick: () => getBillDetails(record),
            };
          }}
        />
      </div>
      {Object.keys(selectedRecord).length !== 0 && (
        <OrdersInfoModal record={selectedRecord} />
      )}
    </>
  );
};

export default OrdersInfoTable;
